/**
 * Theme-key storage helpers.
 *
 * Three sources of truth, each with their own scope:
 *
 *   1. Session theme — picked at random when a user lands on the
 *      sign-in page, kept in sessionStorage until they close the
 *      browser. Used by the Hub (Classrooms / Settings tabs) so the
 *      teacher's first impression theme persists across the session
 *      regardless of any classroom they later configure.
 *
 *   2. Per-classroom theme — chosen by the teacher on a classroom's
 *      General tab. Persisted in localStorage so it survives reloads.
 *      Used by ClassroomDetail and the live morning-meeting view.
 *
 *   3. Teacher preferences — single theme per teacher, stored
 *      server-side. Today still used by the legacy ThemeShell for
 *      live mode; superseded by (2) at the UI layer.
 *
 * Until the backend per-classroom theme column lands, (2) is purely
 * client-side. See docs/CLASSROOM_HUB_PLAN.md.
 */

export const SESSION_THEME_KEY = 'classy:loginThemeKey';
const CLASSROOM_THEME_PREFIX = 'classy:classroomTheme:';
export const DEFAULT_THEME_KEY = 'jungle';

function safeSession(): globalThis.Storage | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

function safeLocal(): globalThis.Storage | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage;
  } catch {
    return null;
  }
}

export function getSessionThemeKey(fallback: string = DEFAULT_THEME_KEY): string {
  const store = safeSession();
  return store?.getItem(SESSION_THEME_KEY) ?? fallback;
}

export function setSessionThemeKey(key: string): void {
  safeSession()?.setItem(SESSION_THEME_KEY, key);
}

export function getClassroomThemeKey(
  classroomId: string,
  fallback: string = DEFAULT_THEME_KEY,
): string {
  const store = safeLocal();
  return store?.getItem(CLASSROOM_THEME_PREFIX + classroomId) ?? fallback;
}

export function setClassroomThemeKey(classroomId: string, key: string): void {
  safeLocal()?.setItem(CLASSROOM_THEME_PREFIX + classroomId, key);
}

/**
 * Per-classroom "show mascot" toggle. Default ON — most rooms keep the
 * mascot since it's a friendly anchor for the room. Older-grade
 * teachers can flip it OFF so the live screens read as a cleaner,
 * less-cartoony space.
 */
const CLASSROOM_SHOW_MASCOT_PREFIX = 'classy:classroomShowMascot:';
export const DEFAULT_SHOW_MASCOT = true;

/**
 * Returns the teacher's explicit choice when set; otherwise `defaultValue`
 * (defaults to DEFAULT_SHOW_MASCOT). The 4–8 home passes `false` so the mascot
 * is off by default there, while still honoring an explicit "show".
 */
export function getClassroomShowMascot(
  classroomId: string,
  defaultValue: boolean = DEFAULT_SHOW_MASCOT,
): boolean {
  const store = safeLocal();
  const raw = store?.getItem(CLASSROOM_SHOW_MASCOT_PREFIX + classroomId);
  if (raw === null || raw === undefined) return defaultValue;
  return raw === '1';
}

export function setClassroomShowMascot(classroomId: string, show: boolean): void {
  safeLocal()?.setItem(CLASSROOM_SHOW_MASCOT_PREFIX + classroomId, show ? '1' : '0');
}
